import { Checkbox, Flex, Form, Input, Modal, Typography } from "antd";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import SelectProduct from "~pages/Users/components/SelectProduct/SelectProduct.tsx";
import { PermissionProducts } from "~pages/Users/enums/permissionProducts";
import type { AddUserForm } from "../types";

import "./AddUserModal.scss";

type Props = {
  onAdd: (userData: AddUserForm) => Promise<unknown>;
  onClose: () => void;
  isAddUserInProgress: boolean;
  isOpen: boolean;
};

const ModalTitle = () => (
  <>
    <Typography.Title level={3}>
      <FormattedMessage id="permissions.user.actions.add.title" />
    </Typography.Title>
    <Typography.Paragraph className="permissions-add-user-modal-subtitle">
      <FormattedMessage id="permissions.user.actions.add.subtitle" />
    </Typography.Paragraph>
  </>
);

const initialValues: Partial<AddUserForm> = { selectedProduct: PermissionProducts.Competitions };

const AddUserModal = ({ onAdd, isOpen, onClose, isAddUserInProgress }: Props) => {
  const { formatMessage } = useIntl();
  const [resetAfterSubmit, setResetAfterSubmit] = useState(false);
  const [form] = Form.useForm<AddUserForm>();

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue(initialValues);
    }
  }, [isOpen, form]);

  const handleSubmit = (values) =>
    onAdd(values).then(() => {
      if (resetAfterSubmit) {
        form.resetFields();
        setResetAfterSubmit(false);
      } else {
        onClose();
      }
    });

  return (
    <Modal
      className="permissions-add-user-modal"
      open={isOpen}
      width={600}
      centered
      onCancel={onClose}
      title={<ModalTitle />}
      okText={formatMessage({ id: "invite" })}
      okButtonProps={{ loading: isAddUserInProgress, htmlType: "submit" }}
      modalRender={(dom) => (
        <Form
          form={form}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          clearOnDestroy
          onFinish={handleSubmit}
          initialValues={initialValues}
        >
          {dom}
        </Form>
      )}
      footer={(originNode) => (
        <Flex align="center" justify="space-between">
          <Checkbox checked={resetAfterSubmit} onChange={(e) => setResetAfterSubmit(e.target.checked)}>
            <FormattedMessage id="permissions.user.actions.add.another" />
          </Checkbox>
          <Flex align="center" gap={8}>
            {originNode}
          </Flex>
        </Flex>
      )}
      destroyOnHidden
    >
      <Form.Item<AddUserForm>
        required
        name="name"
        label={formatMessage({ id: "permissions.user.name" })}
        tooltip={formatMessage({ id: "form.help.permissions.user.add.name" })}
        rules={[
          {
            required: true,
            message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "permissions.user.name" }) }),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<AddUserForm>
        required
        name="email"
        label={formatMessage({ id: "email" })}
        tooltip={formatMessage({ id: "form.help.permissions.user.add.email" })}
        rules={[
          {
            type: "email",
            message: formatMessage({
              id: "form.help.permissions.user.add.email.invalid",
            }),
          },
          {
            required: true,
            message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "email" }) }),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<AddUserForm>
        required
        name="selectedProduct"
        label={formatMessage({ id: "permissions.user.selectProduct" })}
        tooltip={formatMessage({ id: "form.help.selectProduct.on.add.action" })}
        rules={[
          {
            required: true,
            message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "permissions.user.selectProduct" }) }),
          },
        ]}
      >
        <SelectProduct />
      </Form.Item>
    </Modal>
  );
};

export default AddUserModal;
